/**
 * Die Liste: 995 Tauchgaenge, neueste zuerst, mit Suche und Filtern.
 *
 * Zwei Entscheidungen praegen den Aufbau:
 *
 * 1. **Die Profil-Glyphen werden erst gezeichnet, wenn eine Zeile ins Bild
 *    kommt** (IntersectionObserver). Sonst holte der Browser beim Oeffnen 338
 *    Profile aus der Datenbank — fuer Zeilen, die niemand sieht.
 * 2. **Beim Filtern wird nur der Ergebnisbereich neu gebaut, nie die
 *    Suchleiste.** Wer das Eingabefeld bei jedem Tastendruck ersetzt, nimmt dem
 *    Nutzer mitten im Wort den Fokus weg.
 */

import { SPEICHER, alleAus, alleTauchgaenge as ladeTauchgaenge, messpunkte } from '../db.js';
import { datum, tiefe, dauer, uhrzeit, jahr } from '../format.js';
import { zeichne } from '../glyph.js';
import { LANGDRUCK_MS, haeltStill } from '../geste.js';
import { stups } from '../mobil.js';
import { symbol } from '../symbole.js';
import { filtere, mitSuchtext, zuHash } from '../suche.js';
import { geraeteKennung } from '../sync.js';
import { tiefenfarbe } from '../tiefenfarbe.js';
import { zeigeErgebnis } from './ruecknahme.js';
import { oeffneSammelformular } from './sammelformular.js';
import { suchleiste } from './suchleiste.js';
import { frageJahr } from './jahrwahl.js';

function zeile(tg, markiert, beiMarkierung) {
  const el = document.createElement('a');
  el.className = 'zeile';
  el.href = `#/tauchgang/${tg.id}`;
  el.dataset.id = tg.id;
  el.innerHTML = `
    <canvas class="zeile-glyph" width="72" height="40" aria-hidden="true"></canvas>
    <span class="zeile-nummer">${tg.dive_number ?? '—'}</span>
    <span class="zeile-text">
      <span class="zeile-platz"></span>
      <span class="zeile-meta">${datum(tg)} · ${uhrzeit(tg)}</span>
    </span>
    <span class="zeile-werte">
      <span class="zeile-tiefe">${tiefe(tg.max_depth_m)}</span>
      <span class="zeile-dauer">${dauer(tg.duration_s)}</span>
    </span>`;
  el.querySelector('.zeile-platz').textContent = tg.site_name ?? 'ohne Platz';
  el.querySelector('.zeile-tiefe').style.color = tiefenfarbe(tg.max_depth_m);
  if (tg.needs_review) {
    const marke = document.createElement('span');
    marke.className = 'marke';
    marke.textContent = 'prüfen';
    marke.title = tg.review_note ?? '';
    el.querySelector('.zeile-text').append(marke);
  }
  // Haekchen fuer den Auswahlmodus: als Geschwister des Verweises, nicht als
  // Kind. Ein <a> verbietet interaktive Nachfahren (eine Checkbox darin ist
  // ungueltiges HTML) — und ausserhalb des Links gibt es auch keinen Grund
  // mehr, dem Klick per preventDefault()/stopPropagation() die Navigation
  // abzugewoehnen: ein normaler `change` reicht, `checked` stimmt sofort.
  const haken = document.createElement('input');
  haken.type = 'checkbox';
  haken.className = 'zeile-haken';
  haken.checked = markiert.has(tg.id);
  haken.addEventListener('change', () => {
    if (haken.checked) markiert.add(tg.id); else markiert.delete(tg.id);
    beiMarkierung();
  });

  const huelle = document.createElement('div');
  huelle.className = 'zeile-huelle';
  huelle.append(haken, el);
  return huelle;
}

/**
 * Wie viele der Markierten stehen gerade nicht in den Treffern?
 *
 * Die Markierung überlebt einen Filterwechsel, die Häkchen dazu nicht: Wer
 * vierzig markiert und danach die Suche einengt, sähe sonst zwei Häkchen und
 * einen Fuss, der von vierzig spricht. Diese Zahl macht den Unterschied
 * sichtbar.
 */
export function verborgeneMarkierte(markiert, treffer) {
  return markiert.size - treffer.filter((tg) => markiert.has(tg.id)).length;
}

/**
 * Der Weg zu einem neuen Tauchgang.
 *
 * Auf dem Schreibtisch ein Knopf mit Beschriftung, auf dem Telefon derselbe
 * Knopf als runde Scheibe über der Tab-Leiste — dort, wo der Daumen ohnehin
 * liegt. Beides ist dasselbe Element; welche Gestalt es annimmt, entscheidet
 * die Breite in `style.css`, nicht eine zweite Abfrage hier.
 */
function neuKnopf() {
  const neu = document.createElement('a');
  neu.className = 'knopf neuer-tauchgang';
  neu.href = '#/neu';
  neu.setAttribute('aria-label', 'Neuer Tauchgang');
  neu.append(symbol('plus', 26));
  const wort = document.createElement('span');
  wort.className = 'fab-wort';
  wort.textContent = 'Neuer Tauchgang';
  neu.append(wort);
  return neu;
}

/**
 * Langes Drücken auf eine Zeile öffnet den Auswahlmodus.
 *
 * Erst in einen Modus wechseln und dann markieren sind zwei Schritte für
 * etwas, das überall sonst einer ist. Der Knopf bleibt trotzdem stehen: mit
 * der Maus gibt es kein langes Drücken, und wer ihn kennt, sucht ihn.
 *
 * Der Klick nach dem Druck wird geschluckt — sonst öffnete sich zugleich der
 * Tauchgang, den man gerade markiert hat.
 */
function langdruck(behaelter, beiDruck) {
  let uhr = null;
  let startX = 0;
  let startY = 0;
  let zeile = null;
  let ausgeloest = false;

  const abbrechen = () => {
    if (uhr) clearTimeout(uhr);
    uhr = null;
    zeile = null;
  };

  behaelter.addEventListener('pointerdown', (ereignis) => {
    if (ereignis.pointerType === 'mouse') return;
    const treffer = ereignis.target.closest?.('.zeile');
    if (!treffer) return;
    startX = ereignis.clientX;
    startY = ereignis.clientY;
    zeile = treffer;
    ausgeloest = false;
    uhr = setTimeout(() => {
      ausgeloest = true;
      const getroffen = zeile;
      abbrechen();
      if (getroffen) { stups(); beiDruck(getroffen.dataset.id); }
    }, LANGDRUCK_MS);
  }, { passive: true });

  behaelter.addEventListener('pointermove', (ereignis) => {
    if (!uhr) return;
    if (!haeltStill(ereignis.clientX - startX, ereignis.clientY - startY)) abbrechen();
  }, { passive: true });

  behaelter.addEventListener('pointerup', abbrechen, { passive: true });
  behaelter.addEventListener('pointercancel', abbrechen, { passive: true });
  behaelter.addEventListener('click', (ereignis) => {
    if (!ausgeloest) return;
    ausgeloest = false;
    ereignis.preventDefault();
    ereignis.stopPropagation();
  }, true);
}

export function ansichtListe(db, alleTauchgaenge, startKriterien = {}, aufAenderung = null) {
  const alle = mitSuchtext(alleTauchgaenge);
  const wurzel = document.createElement('section');
  wurzel.className = 'liste';

  if (alle.length === 0) {
    wurzel.innerHTML = '<p class="leer">Noch keine Tauchgänge. '
      + '<a href="#/import">Bundle laden</a> — die Datei <code>logbook.json.gz</code> '
      + 'aus <code>out/export/</code> — oder gleich von Hand erfassen.</p>';
    const kopf = document.createElement('div');
    kopf.className = 'listenkopf';
    kopf.append(neuKnopf());
    wurzel.append(kopf);
    return wurzel;
  }

  const listenkopf = document.createElement('div');
  listenkopf.className = 'listenkopf';
  listenkopf.append(neuKnopf());
  wurzel.append(listenkopf);

  // Auswahlmodus: Die Suchleiste bringt Volltext, Platz, Tiefenbereich und die
  // Marken mit — das ist der Gewinn gegenueber der Stammdaten-Tabelle, die nur
  // ueber ihre eigenen Spalten filtern kann.
  const markiert = new Set();
  let auswahlModus = false;

  const auswahlKnopf = document.createElement('button');
  auswahlKnopf.type = 'button';
  auswahlKnopf.className = 'knopf auswahl-an';
  auswahlKnopf.textContent = 'Auswählen';
  // Auf dem Telefon zieht die Hülle den Knopf nach oben rechts in die
  // Kopfzeile — sonst belegt er allein eine Zeile über der Liste.
  auswahlKnopf.dataset.kopfaktion = '';
  auswahlKnopf.addEventListener('click', () => {
    auswahlModus = !auswahlModus;
    if (!auswahlModus) markiert.clear();
    auswahlKnopf.textContent = auswahlModus ? 'Auswahl beenden' : 'Auswählen';
    // Am Finger ist der Knopf ausgeblendet — langes Drücken öffnet den Modus.
    // Heraus muss man aber wieder, also steht er im Modus selbst da.
    auswahlKnopf.classList.toggle('aktiv', auswahlModus);
    wurzel.classList.toggle('auswahlmodus', auswahlModus);
    zeichneErgebnisse();
  });
  listenkopf.append(auswahlKnopf);

  // Der Bericht der Sammelbearbeitung. Er steht nicht im Auswahlfuss: Der ist
  // nach dem Schreiben leer und verschwindet — die Meldung wäre nie zu lesen.
  const meldung = document.createElement('p');
  meldung.className = 'sammelmeldung';
  wurzel.append(meldung);

  let kriterien = { ...startKriterien };
  let beobachter = null;
  let sichtbare = [];      // die aktuellen Treffer — der Fuss zaehlt gegen sie

  const leiste = suchleiste(alle, kriterien, (neu) => {
    kriterien = neu;
    // replaceState statt location.hash: Das Ereignis hashchange wuerde die
    // Ansicht neu aufbauen und den Fokus aus dem Suchfeld nehmen.
    history.replaceState(null, '', zuHash(kriterien));
    // Der Kopf nimmt die Auswahl mit: Wer gefiltert hat und dann auf Statistik
    // klickt, will die Zahlen zu dieser Auswahl, nicht zum ganzen Bestand.
    wurzel.dispatchEvent(new CustomEvent('kriterien', { bubbles: true, detail: kriterien }));
    zeichneErgebnisse();
  });
  const ergebnisse = document.createElement('div');
  ergebnisse.className = 'ergebnisse';
  wurzel.append(leiste, ergebnisse);

  langdruck(ergebnisse, (kennung) => {
    if (!auswahlModus) {
      auswahlModus = true;
      auswahlKnopf.textContent = 'Auswahl beenden';
      wurzel.classList.add('auswahlmodus');
    }
    markiert.add(kennung);
    zeichneErgebnisse();
  });

  const fuss = document.createElement('div');
  fuss.className = 'auswahlfuss';
  fuss.hidden = true;
  wurzel.append(fuss);

  /**
   * Der Auswahlfuss — und die einzige Stelle, die sagt, worauf sich
   * "Bearbeiten" bezieht.
   *
   * Die Markierung ueberlebt einen Filterwechsel: Wer vierzig Tauchgaenge
   * markiert und danach die Suche einengt, hat sie nicht aus Versehen wieder
   * verloren. Dann verschwinden aber die Haekchen, waehrend geschrieben wird —
   * deshalb nennt der Fuss, wie viele der Markierten gerade nicht zu sehen
   * sind, und bietet daneben an, die Auswahl aufzuheben.
   */
  function aktualisiereFuss() {
    fuss.hidden = markiert.size === 0;
    if (markiert.size === 0) { fuss.replaceChildren(); return; }
    const verborgen = verborgeneMarkierte(markiert, sichtbare);
    const text = document.createElement('span');
    text.textContent = verborgen
      ? `${markiert.size} ausgewählt, davon ${verborgen} nicht in den Treffern`
      : `${markiert.size} ausgewählt`;

    const aufheben = document.createElement('button');
    aufheben.type = 'button';
    aufheben.className = 'knopf';
    aufheben.textContent = 'Auswahl aufheben';
    aufheben.addEventListener('click', () => { markiert.clear(); zeichneErgebnisse(); });

    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'knopf haupt';
    knopf.textContent = 'Bearbeiten';
    knopf.addEventListener('click', async () => {
      const markierte = alle.filter((tg) => markiert.has(tg.id));
      const plaetze = await alleAus(db, SPEICHER.sites);
      const ergebnis = await oeffneSammelformular(db, markierte, alle, plaetze,
        { geraet: geraeteKennung() });
      if (!ergebnis) return;
      markiert.clear();
      // Die Liste haelt ihre Tauchgaenge als Kopie und frischt sie an Ort auf.
      // Ein Neuladen der Seite wuerde den Abgleich abwuergen: Der laeuft
      // dreissig Sekunden nach der letzten Aenderung in dieser Seite.
      await frischeDaten();
      zeigeErgebnis(meldung, db, ergebnis,
        { geraet: geraeteKennung(), aufFrischen: frischeDaten });
    });
    fuss.replaceChildren(text, aufheben, knopf);
  }

  /** Nach dem Schreiben: Kopfdaten neu holen, Zwischenspeicher verwerfen. */
  async function frischeDaten() {
    await aufAenderung?.();
    const frisch = mitSuchtext(await ladeTauchgaenge(db));
    alle.length = 0;
    alle.push(...frisch);
    zeichneErgebnisse();
  }

  /**
   * Die Jahresüberschrift — beim Blättern klebt sie oben und ist zugleich der
   * Weg zu jedem anderen Jahr. Zwei Aufgaben in einem Element, aber es ist
   * dieselbe Auskunft: „Du bist in 2024" und „hier kommst du woandershin".
   */
  function jahreskopf(j, anzahl) {
    const kopf = document.createElement('h2');
    kopf.className = 'jahr';
    kopf.dataset.jahr = j ?? '';
    const knopf = document.createElement('button');
    knopf.type = 'button';
    knopf.className = 'jahrsprung';
    knopf.innerHTML = `<span class="jahr-zahl">${j ?? 'ohne Datum'}</span>`
      + `<span class="jahr-anzahl">${anzahl} `
      + `${anzahl === 1 ? 'Tauchgang' : 'Tauchgänge'}</span>`;
    knopf.addEventListener('click', () => springeZuJahr(j));
    kopf.append(knopf);
    return kopf;
  }

  /**
   * Zum gewählten Jahr rollen.
   *
   * Der Zielpunkt liegt um die Höhe der klebenden Kopfzeile tiefer — sonst
   * verschwände die Überschrift, auf die man gerade gezielt hat, genau
   * darunter.
   */
  async function springeZuJahr(aktuelles) {
    const angebot = [...ergebnisse.querySelectorAll('.jahr')].map((kopf) => ({
      jahr: kopf.dataset.jahr || null,
      anzahl: Number(kopf.querySelector('.jahr-anzahl')?.textContent.split(' ')[0] ?? 0),
    }));
    if (angebot.length < 2) return;
    const gewaehlt = await frageJahr(angebot, aktuelles);
    if (gewaehlt === null || gewaehlt === undefined) return;
    const ziel = ergebnisse.querySelector(`.jahr[data-jahr="${gewaehlt ?? ''}"]`);
    if (!ziel) return;
    const klebend = document.querySelector('.kopf')?.offsetHeight ?? 0;
    globalThis.scrollTo({
      top: ziel.getBoundingClientRect().top + globalThis.scrollY - klebend,
      behavior: 'smooth',
    });
  }

  function zeichneErgebnisse() {
    beobachter?.disconnect();
    beobachter = new IntersectionObserver((eintraege) => {
      for (const eintrag of eintraege) {
        if (!eintrag.isIntersecting) continue;
        const el = eintrag.target;
        beobachter.unobserve(el);
        const tg = alle.find((t) => t.id === el.dataset.id);
        const leinwand = el.querySelector('.zeile-glyph');
        messpunkte(db, el.dataset.id)
          .then((werte) => zeichne(leinwand, werte ?? [], tg?.max_depth_m ?? 0))
          .catch(() => zeichne(leinwand, [], tg?.max_depth_m ?? 0));
      }
    }, { rootMargin: '200px' });

    const treffer = filtere(alle, kriterien);
    sichtbare = treffer;
    leiste.zeigeStand(treffer.length, alle.length, kriterien);

    const stapel = document.createDocumentFragment();
    let letztesJahr = null;
    const proJahr = new Map();
    for (const tg of treffer) proJahr.set(jahr(tg), (proJahr.get(jahr(tg)) ?? 0) + 1);

    for (const tg of treffer) {
      const j = jahr(tg);
      if (j !== letztesJahr) {
        letztesJahr = j;
        stapel.append(jahreskopf(j, proJahr.get(j) ?? 0));
      }
      const el = zeile(tg, markiert, aktualisiereFuss);
      stapel.append(el);
    }

    ergebnisse.replaceChildren(stapel);
    for (const el of ergebnisse.querySelectorAll('.zeile')) beobachter.observe(el);

    if (treffer.length === 0) {
      const leer = document.createElement('p');
      leer.className = 'leer';
      leer.textContent = 'Keine Treffer. Weniger Filter oder ein anderer Begriff hilft.';
      ergebnisse.append(leer);
    }

    // Der Fuss haengt an den Treffern: Er nennt, wie viele Markierte gerade
    // nicht zu sehen sind.
    aktualisiereFuss();
  }

  zeichneErgebnisse();
  wurzel.aufraeumen = () => beobachter?.disconnect();
  return wurzel;
}
